import { useState, useEffect } from 'react';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Mail, 
  Phone,
  Settings,
  Send,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RotateCcw,
  Inbox,
  Trash2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { ExpiryAlertSettings } from './notification-settings/ExpiryAlertSettings';

const statusConfig = {
  PENDING: { icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  SENDING: { icon: Loader2, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  SENT: { icon: CheckCircle, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  FAILED: { icon: XCircle, color: 'bg-red-50 text-red-700 border-red-200' },
  DEAD: { icon: AlertCircle, color: 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600' }
};

// Channel Section Component (moved outside to prevent re-renders)
const ChannelSection = ({ 
  channel, 
  icon: Icon, 
  title, 
  settings, 
  showSecrets, 
  updateSetting, 
  toggleSecret, 
  onTestClick, 
  t 
}) => {
  const enabledKey = `${channel}.enabled`;
  const isEnabled = settings[enabledKey];
  
  return (
    <Card className={cn(!isEnabled && "opacity-60")}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "p-2 rounded-lg",
              channel === 'sms' && "bg-emerald-100",
              channel === 'email' && "bg-blue-100",
              channel === 'whatsapp' && "bg-green-100"
            )}>
              <Icon className={cn(
                "h-5 w-5",
                channel === 'sms' && "text-emerald-600",
                channel === 'email' && "text-blue-600",
                channel === 'whatsapp' && "text-green-600"
              )} />
            </div>
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              <CardDescription>
                {isEnabled ? t('notifications.enabled') : t('notifications.disabled')}
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={isEnabled || false}
              onCheckedChange={(checked) => updateSetting(enabledKey, checked)}
            />
            {isEnabled && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onTestClick(channel.toUpperCase())}
              >
                <Send className="h-4 w-4 mr-1" />
                Test
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      {isEnabled && (
        <CardContent className="space-y-4">
          {/* EMAIL - Mandrill Settings */}
          {channel === 'email' && (
            <>
              <div>
                <Label>{t('notifications.emailProvider')}</Label>
                <Select 
                  value={settings['email.provider'] || 'mandrill'} 
                  onValueChange={(v) => updateSetting('email.provider', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mandrill">Mandrill (Mailchimp)</SelectItem>
                    <SelectItem value="smtp">SMTP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {(settings['email.provider'] === 'mandrill' || !settings['email.provider']) && (
                <>
                  <div>
                    <Label>{t('notifications.mandrillApiKey')}</Label>
                    <div className="relative">
                      <Input
                        type={showSecrets['email.mandrill_api_key'] ? 'text' : 'password'}
                        value={settings['email.mandrill_api_key'] || ''}
                        onChange={(e) => updateSetting('email.mandrill_api_key', e.target.value)}
                        placeholder="md-xxxxxx..."
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-0 top-0 h-full"
                        onClick={() => toggleSecret('email.mandrill_api_key')}
                      >
                        {showSecrets['email.mandrill_api_key'] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </>
              )}
              
              {settings['email.provider'] === 'smtp' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('notifications.smtpHost')}</Label>
                      <Input
                        value={settings['email.smtp_host'] || ''}
                        onChange={(e) => updateSetting('email.smtp_host', e.target.value)}
                        placeholder="smtp.example.com"
                      />
                    </div>
                    <div>
                      <Label>{t('notifications.smtpPort')}</Label>
                      <Input
                        type="number"
                        value={settings['email.smtp_port'] || 587}
                        onChange={(e) => updateSetting('email.smtp_port', parseInt(e.target.value))}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('notifications.smtpUser')}</Label>
                      <Input
                        value={settings['email.smtp_user'] || ''}
                        onChange={(e) => updateSetting('email.smtp_user', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>{t('notifications.smtpPass')}</Label>
                      <div className="relative">
                        <Input
                          type={showSecrets['email.smtp_pass'] ? 'text' : 'password'}
                          value={settings['email.smtp_pass'] || ''}
                          onChange={(e) => updateSetting('email.smtp_pass', e.target.value)}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full"
                          onClick={() => toggleSecret('email.smtp_pass')}
                        >
                          {showSecrets['email.smtp_pass'] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* Common email fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('notifications.fromAddress')}</Label>
                  <Input
                    value={settings['email.from'] || ''}
                    onChange={(e) => updateSetting('email.from', e.target.value)}
                    placeholder="noreply@example.com"
                  />
                </div>
                <div>
                  <Label>{t('notifications.fromName')}</Label>
                  <Input
                    value={settings['email.from_name'] || ''}
                    onChange={(e) => updateSetting('email.from_name', e.target.value)}
                    placeholder="OMS System"
                  />
                </div>
              </div>
              <div>
                <Label>{t('notifications.replyTo')}</Label>
                <Input
                  value={settings['email.reply_to'] || ''}
                  onChange={(e) => updateSetting('email.reply_to', e.target.value)}
                  placeholder="hr@example.com"
                />
              </div>
            </>
          )}
          
          {/* SMS - Custom HTTP API */}
          {channel === 'sms' && (
            <>
              <div>
                <Label>{t('notifications.providerUrl')}</Label>
                <Input
                  value={settings['sms.provider_url'] || ''}
                  onChange={(e) => updateSetting('sms.provider_url', e.target.value)}
                  placeholder="https://api.smsprovider.com/send"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t('notifications.smsApiNote')}
                </p>
              </div>
              <div>
                <Label>{t('notifications.apiKey')}</Label>
                <div className="relative">
                  <Input
                    type={showSecrets['sms.api_key'] ? 'text' : 'password'}
                    value={settings['sms.api_key'] || ''}
                    onChange={(e) => updateSetting('sms.api_key', e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => toggleSecret('sms.api_key')}
                  >
                    {showSecrets['sms.api_key'] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('notifications.sender')}</Label>
                  <Input
                    value={settings['sms.sender'] || ''}
                    onChange={(e) => updateSetting('sms.sender', e.target.value)}
                    placeholder="+905551234567"
                  />
                </div>
                <div>
                  <Label>{t('notifications.defaultMesajTuru')}</Label>
                  <Select 
                    value={settings['sms.mesaj_turu'] || 'bilgilendirme'} 
                    onValueChange={(v) => updateSetting('sms.mesaj_turu', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bilgilendirme">Bilgilendirme</SelectItem>
                      <SelectItem value="onay">Onay</SelectItem>
                      <SelectItem value="red">Red</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
          
          {/* WhatsApp - Custom HTTP API */}
          {channel === 'whatsapp' && (
            <>
              <div>
                <Label>{t('notifications.providerUrl')}</Label>
                <Input
                  value={settings['whatsapp.provider_url'] || ''}
                  onChange={(e) => updateSetting('whatsapp.provider_url', e.target.value)}
                  placeholder="https://api.whatsappprovider.com/send"
                />
              </div>
              <div>
                <Label>{t('notifications.apiKey')}</Label>
                <div className="relative">
                  <Input
                    type={showSecrets['whatsapp.api_key'] ? 'text' : 'password'}
                    value={settings['whatsapp.api_key'] || ''}
                    onChange={(e) => updateSetting('whatsapp.api_key', e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={() => toggleSecret('whatsapp.api_key')}
                  >
                    {showSecrets['whatsapp.api_key'] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <Label>{t('notifications.sender')}</Label>
                <Input
                  value={settings['whatsapp.sender'] || ''}
                  onChange={(e) => updateSetting('whatsapp.sender', e.target.value)}
                  placeholder="WhatsApp Business ID"
                />
              </div>
            </>
          )}
          
          {/* Common timeout field */}
          <div>
            <Label>{t('notifications.timeout')}</Label>
            <Input
              type="number"
              value={settings[`${channel}.timeout_ms`] || 8000}
              onChange={(e) => updateSetting(`${channel}.timeout_ms`, parseInt(e.target.value))}
            />
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export const NotificationSettings = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const [activeTab, setActiveTab] = useState('settings');
  const [showSecrets, setShowSecrets] = useState({});
  
  // Outbox state
  const [outboxItems, setOutboxItems] = useState([]);
  const [outboxLoading, setOutboxLoading] = useState(false);
  const [outboxFilter, setOutboxFilter] = useState({ status: 'all', channel: 'all', search: '', date_from: '', date_to: '' });
  const [outboxPage, setOutboxPage] = useState(1);
  const [outboxTotal, setOutboxTotal] = useState(0);
  const [outboxTotalPages, setOutboxTotalPages] = useState(1);
  const OUTBOX_PAGE_SIZE = 15;
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Test send state
  const [testDialog, setTestDialog] = useState({ open: false, channel: '' });
  const [testTo, setTestTo] = useState('');
  const [testMessage, setTestMessage] = useState('Test notification from OMS System');
  const [testSending, setTestSending] = useState(false);

  useEffect(() => {
    fetchSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'outbox') {
      fetchOutbox();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, outboxFilter, outboxPage]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/notifications/settings');
      if (response.data.success) {
        setSettings(response.data.data.settings || {});
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchOutbox = async () => {
    setOutboxLoading(true);
    try {
      let url = `/admin/notifications/outbox?page=${outboxPage}&limit=${OUTBOX_PAGE_SIZE}`;
      if (outboxFilter.status !== 'all') url += `&status=${outboxFilter.status}`;
      if (outboxFilter.channel !== 'all') url += `&channel=${outboxFilter.channel}`;
      if (outboxFilter.search) url += `&search=${encodeURIComponent(outboxFilter.search)}`;
      if (outboxFilter.date_from) url += `&date_from=${outboxFilter.date_from}`;
      if (outboxFilter.date_to) url += `&date_to=${outboxFilter.date_to}`;
      
      const response = await api.get(url);
      if (response.data.success) {
        setOutboxItems(response.data.data.items || []);
        setOutboxTotal(response.data.data.total || 0);
        setOutboxTotalPages(response.data.data.total_pages || 1);
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setOutboxLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        // SMS Settings - Custom HTTP API
        sms_enabled: settings['sms.enabled'],
        sms_provider_url: settings['sms.provider_url'],
        sms_api_key: settings['sms.api_key']?.startsWith('**') ? undefined : settings['sms.api_key'],
        sms_sender: settings['sms.sender'],
        sms_timeout_ms: settings['sms.timeout_ms'],
        sms_mesaj_turu: settings['sms.mesaj_turu'],
        
        // Email Settings - Mandrill
        email_enabled: settings['email.enabled'],
        email_provider: settings['email.provider'],
        email_mandrill_api_key: settings['email.mandrill_api_key']?.startsWith('**') ? undefined : settings['email.mandrill_api_key'],
        email_from: settings['email.from'],
        email_from_name: settings['email.from_name'],
        email_reply_to: settings['email.reply_to'],
        email_timeout_ms: settings['email.timeout_ms'],
        // Legacy SMTP
        email_smtp_host: settings['email.smtp_host'],
        email_smtp_port: settings['email.smtp_port'],
        email_smtp_user: settings['email.smtp_user'],
        email_smtp_pass: settings['email.smtp_pass']?.startsWith('**') ? undefined : settings['email.smtp_pass'],
        
        // WhatsApp Settings
        whatsapp_enabled: settings['whatsapp.enabled'],
        whatsapp_provider_url: settings['whatsapp.provider_url'],
        whatsapp_api_key: settings['whatsapp.api_key']?.startsWith('**') ? undefined : settings['whatsapp.api_key'],
        whatsapp_sender: settings['whatsapp.sender'],
        whatsapp_timeout_ms: settings['whatsapp.timeout_ms'],
      };
      
      // Remove undefined values
      Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);
      
      const response = await api.put('/admin/notifications/settings', payload);
      if (response.data.success) {
        toast.success(t('notifications.settingsSaved'));
        fetchSettings();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestSend = async () => {
    if (!testTo) {
      toast.error(t('validation.required'));
      return;
    }
    setTestSending(true);
    try {
      const response = await api.post('/admin/notifications/test', {
        channel: testDialog.channel,
        to: testTo,
        message: testMessage
      });
      if (response.data.success) {
        toast.success(t('notifications.testSent'));
        setTestDialog({ open: false, channel: '' });
        setTestTo('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setTestSending(false);
    }
  };

  const handleRetry = async (id, resetAttempts = false) => {
    try {
      const url = `/admin/notifications/outbox/${id}/retry${resetAttempts ? '?reset_attempts=true' : ''}`;
      const response = await api.put(url);
      if (response.data.success) {
        toast.success(t('notifications.retryQueued'));
        fetchOutbox();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const viewNotificationDetail = async (id) => {
    try {
      const response = await api.get(`/admin/notifications/outbox/${id}`);
      if (response.data.success) {
        setSelectedNotification(response.data.data);
        setDetailDialog(true);
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('notifications.confirmDelete') || 'Bu bildirimi silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      const response = await api.delete(`/admin/notifications/outbox/${id}`);
      if (response.data.success) {
        toast.success(t('notifications.deleted') || 'Bildirim silindi');
        fetchOutbox();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error(t('notifications.selectToDelete') || 'Silmek için bildirim seçin');
      return;
    }
    if (!window.confirm(`${selectedIds.length} bildirimi silmek istediğinizden emin misiniz?`)) {
      return;
    }
    setDeleteLoading(true);
    try {
      const response = await api.post('/admin/notifications/outbox/bulk-delete', { ids: selectedIds });
      if (response.data.success) {
        toast.success(`${response.data.data.deleted_count} bildirim silindi`);
        setSelectedIds([]);
        fetchOutbox();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === outboxItems.length) {
      setSelectedIds([]);
    } else {
      // Don't select SENDING items
      setSelectedIds(outboxItems.filter(item => item.status !== 'SENDING').map(item => item.id));
    }
  };

  const toggleSelectItem = (id, status) => {
    if (status === 'SENDING') return; // Can't select sending items
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleSecret = (key) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTestClick = (channel) => {
    setTestDialog({ open: true, channel });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('notifications.title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t('notifications.subtitle')}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            {t('notifications.settings')}
          </TabsTrigger>
          <TabsTrigger value="outbox" className="gap-2">
            <Inbox className="h-4 w-4" />
            {t('notifications.outbox')}
          </TabsTrigger>
          {user?.role === 'SUPER_ADMIN' && (
            <TabsTrigger value="expiry" className="gap-2" data-testid="expiry-alerts-tab">
              <Clock className="h-4 w-4" />
              Belge Süresi Uyarıları
            </TabsTrigger>
          )}
        </TabsList>

        {user?.role === 'SUPER_ADMIN' && (
          <TabsContent value="expiry" className="mt-6">
            <ExpiryAlertSettings />
          </TabsContent>
        )}

        <TabsContent value="settings" className="mt-6">
          <div className="space-y-6">
            <ChannelSection 
              channel="email" 
              icon={Mail} 
              title="Email" 
              settings={settings}
              showSecrets={showSecrets}
              updateSetting={updateSetting}
              toggleSecret={toggleSecret}
              onTestClick={handleTestClick}
              t={t}
            />
            <ChannelSection 
              channel="sms" 
              icon={Phone} 
              title="SMS" 
              settings={settings}
              showSecrets={showSecrets}
              updateSetting={updateSetting}
              toggleSecret={toggleSecret}
              onTestClick={handleTestClick}
              t={t}
            />
            <ChannelSection 
              channel="whatsapp" 
              icon={MessageSquare} 
              title="WhatsApp" 
              settings={settings}
              showSecrets={showSecrets}
              updateSetting={updateSetting}
              toggleSecret={toggleSecret}
              onTestClick={handleTestClick}
              t={t}
            />
            
            <div className="flex justify-end">
              <Button onClick={saveSettings} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('common.save')}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="outbox" className="mt-6">
          {/* Filters Row 1 */}
          <div className="flex flex-wrap gap-3 mb-3" data-testid="outbox-filters">
            <Select value={outboxFilter.status} onValueChange={(v) => { setOutboxFilter(prev => ({ ...prev, status: v })); setOutboxPage(1); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tum Durumlar</SelectItem>
                <SelectItem value="PENDING">Bekliyor</SelectItem>
                <SelectItem value="SENT">Gonderildi</SelectItem>
                <SelectItem value="FAILED">Basarisiz</SelectItem>
                <SelectItem value="DEAD">Oldu</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={outboxFilter.channel} onValueChange={(v) => { setOutboxFilter(prev => ({ ...prev, channel: v })); setOutboxPage(1); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Kanal" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tum Kanallar</SelectItem>
                <SelectItem value="SMS">SMS</SelectItem>
                <SelectItem value="EMAIL">Email</SelectItem>
                <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="text"
              placeholder="Ara (alici, olay)..."
              value={outboxFilter.search}
              onChange={(e) => { setOutboxFilter(prev => ({ ...prev, search: e.target.value })); setOutboxPage(1); }}
              className="w-[200px]"
              data-testid="outbox-search-input"
            />
            
            <Button variant="outline" size="icon" onClick={fetchOutbox} disabled={outboxLoading} data-testid="outbox-refresh-btn">
              <RefreshCw className={cn("h-4 w-4", outboxLoading && "animate-spin")} />
            </Button>

            {selectedIds.length > 0 && (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleBulkDelete}
                disabled={deleteLoading}
                className="ml-auto"
              >
                {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
                {selectedIds.length} Sil
              </Button>
            )}
          </div>

          {/* Filters Row 2 - Date */}
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-500 whitespace-nowrap">Tarih:</Label>
              <Input
                type="date"
                value={outboxFilter.date_from}
                onChange={(e) => { setOutboxFilter(prev => ({ ...prev, date_from: e.target.value })); setOutboxPage(1); }}
                className="w-[160px] h-9"
                data-testid="outbox-date-from"
              />
              <span className="text-slate-400">-</span>
              <Input
                type="date"
                value={outboxFilter.date_to}
                onChange={(e) => { setOutboxFilter(prev => ({ ...prev, date_to: e.target.value })); setOutboxPage(1); }}
                className="w-[160px] h-9"
                data-testid="outbox-date-to"
              />
            </div>
            {(outboxFilter.date_from || outboxFilter.date_to || outboxFilter.search) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setOutboxFilter({ status: 'all', channel: 'all', search: '', date_from: '', date_to: '' }); setOutboxPage(1); }}
                className="text-slate-500"
              >
                Filtreleri Temizle
              </Button>
            )}
            <div className="ml-auto text-sm text-slate-500">
              Toplam: <span className="font-medium text-slate-700 dark:text-slate-300">{outboxTotal}</span> kayit
            </div>
          </div>

          {/* Outbox Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      checked={outboxItems.length > 0 && selectedIds.length === outboxItems.filter(i => i.status !== 'SENDING').length}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                  </TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead>{t('notifications.channel')}</TableHead>
                  <TableHead>{t('notifications.event')}</TableHead>
                  <TableHead>{t('notifications.recipient')}</TableHead>
                  <TableHead>{t('notifications.status')}</TableHead>
                  <TableHead>{t('notifications.attempts')}</TableHead>
                  <TableHead>{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {outboxLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                    </TableCell>
                  </TableRow>
                ) : outboxItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500 dark:text-slate-400">
                      {t('common.noData')}
                    </TableCell>
                  </TableRow>
                ) : (
                  outboxItems.map(item => {
                    const config = statusConfig[item.status] || statusConfig.PENDING;
                    const StatusIcon = config.icon;
                    
                    return (
                      <TableRow key={item.id} className={selectedIds.includes(item.id) ? 'bg-slate-50 dark:bg-slate-800' : ''}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(item.id)}
                            onChange={() => toggleSelectItem(item.id, item.status)}
                            disabled={item.status === 'SENDING'}
                            className="h-4 w-4 rounded border-slate-300"
                          />
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <div className="text-sm">
                            {item.created_at ? (
                              <>
                                <div className="font-medium text-slate-700 dark:text-slate-300">
                                  {new Date(item.created_at).toLocaleDateString('tr-TR')}
                                </div>
                                <div className="text-xs text-slate-400">
                                  {new Date(item.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </>
                            ) : '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.channel}</Badge>
                        </TableCell>
                        <TableCell className="font-medium text-sm max-w-[150px] truncate">{item.event}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-sm">{item.user_name}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[180px]">{item.to}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={config.color}>
                            <StatusIcon className={cn("h-3 w-3 mr-1", item.status === 'SENDING' && "animate-spin")} />
                            {item.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{item.attempt_count}/{item.max_attempts || 3}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => viewNotificationDetail(item.id)}
                              title={t('notifications.details')}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {item.status === 'FAILED' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleRetry(item.id, false)}
                                title={t('notifications.retry')}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                            {item.status === 'DEAD' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-amber-600"
                                onClick={() => handleRetry(item.id, true)}
                                title={t('notifications.retryWithReset')}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            )}
                            {item.status !== 'SENDING' && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDelete(item.id)}
                                title={t('common.delete') }
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {outboxTotalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t" data-testid="outbox-pagination">
                <div className="text-sm text-slate-500">
                  Sayfa {outboxPage} / {outboxTotalPages} ({outboxTotal} kayit)
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={outboxPage <= 1}
                    onClick={() => setOutboxPage(1)}
                    className="h-8 px-2"
                  >
                    &laquo;
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={outboxPage <= 1}
                    onClick={() => setOutboxPage(p => Math.max(1, p - 1))}
                    className="h-8 px-3"
                  >
                    Onceki
                  </Button>
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, outboxTotalPages) }, (_, i) => {
                    let pageNum;
                    if (outboxTotalPages <= 5) {
                      pageNum = i + 1;
                    } else if (outboxPage <= 3) {
                      pageNum = i + 1;
                    } else if (outboxPage >= outboxTotalPages - 2) {
                      pageNum = outboxTotalPages - 4 + i;
                    } else {
                      pageNum = outboxPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={outboxPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setOutboxPage(pageNum)}
                        className="h-8 w-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={outboxPage >= outboxTotalPages}
                    onClick={() => setOutboxPage(p => Math.min(outboxTotalPages, p + 1))}
                    className="h-8 px-3"
                  >
                    Sonraki
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={outboxPage >= outboxTotalPages}
                    onClick={() => setOutboxPage(outboxTotalPages)}
                    className="h-8 px-2"
                  >
                    &raquo;
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Test Send Dialog */}
      <Dialog open={testDialog.open} onOpenChange={(open) => setTestDialog({ open, channel: testDialog.channel })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('notifications.testSend')} - {testDialog.channel}</DialogTitle>
            <DialogDescription>
              {t('notifications.testDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>{testDialog.channel === 'EMAIL' ? t('notifications.emailAddress') : t('notifications.phoneNumber')}</Label>
              <Input
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder={testDialog.channel === 'EMAIL' ? 'test@example.com' : '+905551234567'}
              />
            </div>
            <div>
              <Label>{t('notifications.message')}</Label>
              <Input
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestDialog({ open: false, channel: '' })}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleTestSend} disabled={testSending}>
              {testSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Send className="mr-2 h-4 w-4" />
              {t('notifications.send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('notifications.details')}</DialogTitle>
          </DialogHeader>
          
          {selectedNotification && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('notifications.channel')}</Label>
                  <p className="font-medium">{selectedNotification.channel}</p>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('notifications.event')}</Label>
                  <p className="font-medium">{selectedNotification.event}</p>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('notifications.recipient')}</Label>
                  <p className="font-medium">{selectedNotification.to}</p>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('notifications.status')}</Label>
                  <Badge variant="outline" className={statusConfig[selectedNotification.status]?.color}>
                    {selectedNotification.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">Olusturulma Tarihi</Label>
                  <p className="font-medium">{selectedNotification.created_at ? new Date(selectedNotification.created_at).toLocaleString('tr-TR') : '-'}</p>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">Guncelleme Tarihi</Label>
                  <p className="font-medium">{selectedNotification.updated_at ? new Date(selectedNotification.updated_at).toLocaleString('tr-TR') : '-'}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-slate-500 dark:text-slate-400">{t('notifications.message')}</Label>
                <div className="mt-1 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm whitespace-pre-wrap">
                  {selectedNotification.template_snapshot?.body}
                </div>
              </div>
              
              {selectedNotification.last_error && (
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('notifications.lastError')}</Label>
                  <div className="mt-1 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                    {selectedNotification.last_error}
                  </div>
                </div>
              )}
              
              {selectedNotification.attempts?.length > 0 && (
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('notifications.attempts')}</Label>
                  <div className="mt-2 space-y-2">
                    {selectedNotification.attempts.map((attempt, idx) => (
                      <div key={idx} className="p-2 bg-slate-50 dark:bg-slate-800 rounded text-sm flex justify-between">
                        <span>#{attempt.attempt_no} - {attempt.status}</span>
                        <span className="text-slate-500 dark:text-slate-400">{new Date(attempt.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialog(false)}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationSettings;
